import React, { Suspense } from 'react';
// import PropTypes from 'prop-types';

import { Loading, NotFoundPage, FooterAdmin } from '../../dronebee-ui';
import { useSelector } from 'react-redux';
import { createBrowserHistory } from 'history';
import { Route, Routes, Navigate, Outlet } from 'react-router-dom';

import NavBar from '../components/NavBar';
import {
  AppWrapper,
  CustomRouter,
  lazyWithRetry,
  PermissionRoute,
  PrivateRoute,
  PublicRoute,
} from './helpers';

const config = programConfig;

// AUTH
const Register = lazyWithRetry(() => import('../components/auth/Register'));
const LogIn = lazyWithRetry(() => import('../components/auth/LogIn'));
const ValidateMFA = lazyWithRetry(() => import('../components/auth/ValidateMFA'));
const ForgotPassword = lazyWithRetry(() => import('../components/auth/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('../components/auth/ResetPassword'));

// APPLICANTS
const AllProperties = lazyWithRetry(() => import('../components/applicants/AllProperties'));
const ApplicantDetail = lazyWithRetry(() => import('../components/applicants/ApplicantDetail'));
const ApplicationDetail = lazyWithRetry(() => import('../components/applicants/ApplicationDetail'));

// APPLICANT TABS
const ApplicantStatus = lazyWithRetry(() => import('../components/applicants/tabs/Status'));
const ApplicantCommunications = lazyWithRetry(() => import('../components/applicants/tabs/Communications'));
const ApplicantChecklists = lazyWithRetry(() => import('../components/applicants/tabs/Checklists'));
const ApplicantLetters = lazyWithRetry(() => import('../components/applicants/tabs/Letters'));
const ApplicantHomeDetails = lazyWithRetry(() => import('../components/applicants/tabs/HomeDetails'));
const ApplicantInsurance = lazyWithRetry(() => import('../components/applicants/tabs/Insurance'));
const ApplicantMortgageLien = lazyWithRetry(() => import('../components/applicants/tabs/MortgageLien'));
const ApplicantHomeowners = lazyWithRetry(() => import('../components/applicants/tabs/Homeowners'));
const ApplicantDocuments = lazyWithRetry(() => import('../components/applicants/tabs/Documents'));
const ApplicantSignatures = lazyWithRetry(() => import('../components/applicants/tabs/Signatures'));

// REPORTS
const IndexReports = lazyWithRetry(() => import('../components/reports/IndexReports'));
const AllApplicationsReport = lazyWithRetry(() => import('../components/reports/pages/AllApplicationsReport'));
const StatusHistoriesReport = lazyWithRetry(() => import('../components/reports/pages/StatusHistoriesReport'));

// SETTINGS
const IndexSettings = lazyWithRetry(() => import('../components/settings/IndexSettings'));
const MySettings = lazyWithRetry(() => import('../components/settings/my-settings/MySettings'));
const ProfileInfo = lazyWithRetry(() => import('../components/settings/my-settings/ProfileInfo'));
const Sessions = lazyWithRetry(() => import('../components/settings/my-settings/Sessions'));
const IndexUserSettings = lazyWithRetry(() => import('../components/settings/user-settings/IndexUserSettings'));
const AllUsers = lazyWithRetry(() => import('../components/settings/user-settings/AllUsers'));
const Teams = lazyWithRetry(() => import('../components/settings/user-settings/Teams'));
const UserPermissionGroups = lazyWithRetry(() => import('../components/settings/user-settings/PermissionGroups'));

// SETTINGS / SYSTEM CONFIG
const SystemConfigIndex = lazyWithRetry(() => import('../components/settings/system-config/SystemConfigIndex'));
const ProcessorStatuses = lazyWithRetry(() => import('../components/settings/system-config/ProcessorStatuses'));
const Letters = lazyWithRetry(() => import('../components/settings/system-config/Letters'));
const ChecklistTemplates = lazyWithRetry(() => import('../components/settings/system-config/ChecklistTemplates'));
const AddChecklistTemplate = lazyWithRetry(() => import('../components/settings/system-config/AddChecklistTemplate'));
const EditChecklistTemplate = lazyWithRetry(() => import('../components/settings/system-config/EditChecklistTemplate'));
const CommunicationTemplates = lazyWithRetry(() => import('../components/settings/system-config/CommunicationTemplates'));
const StatusNoteTemplates = lazyWithRetry(() => import('../components/settings/system-config/StatusNoteTemplates'));
const DocumentNoteTemplates = lazyWithRetry(() => import('../components/settings/system-config/DocumentNoteTemplates'));
const AcceptingApplications = lazyWithRetry(() => import('../components/settings/system-config/AcceptingApplications'));
const Programs = lazyWithRetry(() => import('../components/settings/system-config/Programs'));
const FlagsConfig = lazyWithRetry(() => import('../components/settings/system-config/Flags'));
const ESignConfig = lazyWithRetry(() => import('../components/settings/system-config/ESign'));
const InsuranceCompanies = lazyWithRetry(() => import('../components/settings/system-config/InsuranceCompanies'));
const AddEditInsuranceCompany = lazyWithRetry(() => import('../components/settings/system-config/insurance-companies-pages/AddEditInsuranceCompany'));

// SETTINGS / SYSTEM CONFIG - DROPDOWN SECTIONS
const ParcelIds = lazyWithRetry(() => import('../components/settings/system-config/dropdown-options/ParcelIds'));
const PropertyTypes = lazyWithRetry(() => import('../components/settings/system-config/dropdown-options/PropertyTypes'));
const Services = lazyWithRetry(() => import('../components/settings/system-config/dropdown-options/Services'));
const UserIneligibleReasons = lazyWithRetry(() => import('../components/settings/system-config/dropdown-options/UserIneligibleReasons'));
const UserWithdrawReasons = lazyWithRetry(() => import('../components/settings/system-config/dropdown-options/UserWithdrawReasons'));
const ZipCodes = lazyWithRetry(() => import('../components/settings/system-config/dropdown-options/ZipCodes'));
const Counties = lazyWithRetry(() => import('../components/settings/system-config/dropdown-options/Counties'));
const HowdYouHear = lazyWithRetry(() => import('../components/settings/system-config/dropdown-options/HowdYouHear'));

// TEST
const TestPage = lazyWithRetry(() => import('../components/TestPage'));

export const history = createBrowserHistory();

const AppRouter = () => {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const permissions = useSelector((state) => state.auth.currentUser.permissions);

  return (
    <CustomRouter history={history}>
      <div className="container-scroller">
        <NavBar
          isLoggedIn={isLoggedIn}
        />

        <AppWrapper>
          <Suspense fallback={<Loading main={true} />}>
            <Routes>
              {/* AUTH */}
              <Route path='/login' element={<PublicRoute><LogIn /></PublicRoute>} />
              <Route path='/register/:token_uuid' element={<PublicRoute><Register /></PublicRoute>} />
              <Route path='/validate/session' element={<PublicRoute><ValidateMFA /></PublicRoute>} />
              <Route path='/forgot-password' element={<PublicRoute><ForgotPassword /></PublicRoute>} />
              <Route path='/reset-password/:token_uuid' element={<PublicRoute><ResetPassword /></PublicRoute>} />

              {/* SIGN_UP - without token always reject */}
              <Route path={'/register/:token'} element={<PublicRoute><Register /></PublicRoute>} />

              {/* APPLICANTS */}
              <Route path={'/applications'} element={<PermissionRoute perform={['applicant:read-all', 'applicant:read-team', 'applicant:read-assigned']}><AllProperties /></PermissionRoute>} />
              <Route path={'/applicant/:users_applicant_uuid'} element={<PermissionRoute perform={['applicant:read-all', 'applicant:read-team', 'applicant:read-assigned']}><ApplicantDetail /></PermissionRoute>} />
              <Route
                path={'/applications/:application_uuid'}
                element={<PermissionRoute perform={['applicant:read-all', 'applicant:read-team', 'applicant:read-assigned']}><ApplicationDetail /></PermissionRoute>}
              >
                <Route index element={<Navigate to='status' replace />} />
                <Route path={'status'} element={<PermissionRoute permissions={permissions} perform={['applicant:read-all', 'applicant:read-team', 'applicant:read-assigned']}><ApplicantStatus /></PermissionRoute>}/>
                <Route path={'communications'} element={<PermissionRoute permissions={permissions} perform='applicant-message:read'><ApplicantCommunications /></PermissionRoute>}/>
                <Route path={'checklists'} element={<PermissionRoute permissions={permissions} perform='applicant-checklist:read'><ApplicantChecklists /></PermissionRoute>}/>
                <Route path={'letters'} element={<PermissionRoute permissions={permissions} perform='applicant-letter:read'><ApplicantLetters /></PermissionRoute>}/>
                <Route path={'home-details'} element={<PermissionRoute permissions={permissions} perform={['applicant:read-all', 'applicant:read-team', 'applicant:read-assigned']}><ApplicantHomeDetails history={history}/></PermissionRoute>}/>
                <Route path={'mortgage-lien'} element={<PermissionRoute permissions={permissions} perform={['applicant:read-all', 'applicant:read-team', 'applicant:read-assigned']}><ApplicantMortgageLien history={history}/></PermissionRoute>}/>
                <Route path={'insurance'} element={<PermissionRoute permissions={permissions} perform={['applicant:read-all', 'applicant:read-team', 'applicant:read-assigned']}><ApplicantInsurance history={history}/></PermissionRoute>}/>
                <Route path={'property-owners'} element={<PermissionRoute permissions={permissions} perform={['applicant:read-all', 'applicant:read-team', 'applicant:read-assigned']}><ApplicantHomeowners history={history} /></PermissionRoute>}/>
                <Route path={'documents'} element={<PermissionRoute permissions={permissions} perform={['applicant:read-all', 'applicant:read-team', 'applicant:read-assigned']}><ApplicantDocuments /></PermissionRoute>}/>
                <Route path={'signatures'} element={<PermissionRoute permissions={permissions} perform='applicant-signature:read'><ApplicantSignatures /></PermissionRoute>}/>
              </Route>

              {/* REPORTS */}
              <Route path='/reports' element={<PermissionRoute permissions={permissions} perform='report:read'><IndexReports /></PermissionRoute>} />
              <Route path='/reports/all-applications' element={<PermissionRoute permissions={permissions} perform={['report:read', 'report-applications:download']}><AllApplicationsReport /></PermissionRoute>} />
              <Route path='/reports/status-history' element={<PermissionRoute permissions={permissions} perform={['report:read', 'report-status-history:download']}><StatusHistoriesReport /></PermissionRoute>} />

              {/* SETTINGS */}
              <Route
                path='/settings'
                element={<PrivateRoute><IndexSettings /></PrivateRoute>}
              >
                <Route index element={<Navigate to='my-settings' replace />} />
                <Route path='my-settings' element={<MySettings />} />
                <Route path='user-settings'   element={<PermissionRoute perform={'user-settings:read'}><IndexUserSettings /></PermissionRoute>} />
                <Route path='system-config' element={<PermissionRoute perform={'config:read'}><SystemConfigIndex /></PermissionRoute>} />
              </Route>
              <Route path='/settings/my-settings/profile-info' element={<PrivateRoute><ProfileInfo /></PrivateRoute>} />
              <Route path='/settings/my-settings/sessions' element={<PrivateRoute><Sessions /></PrivateRoute>} />
              <Route path='/settings/user-settings' >
                <Route path='all-users' element={<PermissionRoute perform={'user-settings:read'}><AllUsers /></PermissionRoute>} />
                <Route path='teams' element={<PermissionRoute perform={'user-settings:read'}><Teams /></PermissionRoute>} />
                <Route path='permissions' element={<PermissionRoute perform={'user-settings:write'}><UserPermissionGroups /></PermissionRoute>} />
              </Route>

              {/* SETTINGS - SYSTEM CONFIG */}
              <Route
                path='/settings/system-config'
                element={<PermissionRoute perform={'config:read'}><Outlet /></PermissionRoute>}
              >
                <Route path='/settings/system-config/processor-statuses' element={<ProcessorStatuses />} />
                <Route path='/settings/system-config/letters' element={<Letters />} />
                <Route path='/settings/system-config/checklists' element={<ChecklistTemplates />} />
                <Route path='/settings/system-config/checklists/add' element={<PermissionRoute perform={'config-checklist:edit'}><AddChecklistTemplate /></PermissionRoute>} />
                <Route path='/settings/system-config/checklists/edit/:checklist_uuid' element={<PermissionRoute perform={'config-checklist:edit'}><EditChecklistTemplate /></PermissionRoute>} />
                <Route path='/settings/system-config/communication-templates' element={<CommunicationTemplates />} />
                <Route path='/settings/system-config/status-note-templates' element={<StatusNoteTemplates />} />
                <Route path='/settings/system-config/document-note-templates' element={<DocumentNoteTemplates />} />
                <Route path='/settings/system-config/accepting-applications' element={<AcceptingApplications />} />
                <Route path='/settings/system-config/programs' element={<Programs />} />
                <Route path='/settings/system-config/flags' element={<FlagsConfig />} />
                <Route path='/settings/system-config/e-sign' element={<ESignConfig />} />
                <Route path='/settings/system-config/insurance-companies' element={<InsuranceCompanies />} />
                <Route path='/settings/system-config/insurance-companies/add' element={<PermissionRoute perform={'config-insurance-company:edit'}><AddEditInsuranceCompany /></PermissionRoute>} />
                <Route path='/settings/system-config/insurance-companies/:insurance_company_uuid' element={<PermissionRoute perform={'config:read'}><AddEditInsuranceCompany /></PermissionRoute>} />

                {/* SETTINGS / SYSTEM CONFIG - DROPDOWN OPTIONS */}
                <Route path='/settings/system-config/parcel-ids' element={<ParcelIds />} />
                <Route path='/settings/system-config/user-ineligible-reasons' element={<UserIneligibleReasons />} />
                <Route path='/settings/system-config/user-withdraw-reasons' element={<UserWithdrawReasons />} />
                <Route path='/settings/system-config/property-types' element={<PropertyTypes />} />
                <Route path='/settings/system-config/services' element={<Services />} />
                <Route path='/settings/system-config/zip-codes' element={<ZipCodes />} />
                <Route path='/settings/system-config/counties' element={<Counties />} />
                <Route path='/settings/system-config/howd-you-hear' element={<HowdYouHear />} />
              </Route>

              {/* TEST */}
              {environment !== 'production' && <Route path='/test' element={<PublicRoute><TestPage /></PublicRoute>} />}

              <Route path='*' element={<NotFoundPage onClick={() => history.push('/login')} />} />
            </Routes>
          </Suspense>

        </AppWrapper>
        <FooterAdmin config={config} />
        {environment !== 'production' && <div className='footer-version'>Version {VERSION}</div>}
      </div>
    </CustomRouter>
  );
};

AppRouter.propTypes = {
};

export default AppRouter;
